import {
  DocumentTile,
  KundenDokument,
} from '@codefluegel/wowis-rest-typescript-client';
import { useEffect, useState } from 'react';
import { useStore } from '../store/context';
import { useDocumentApi } from '../ApiClient';

export interface KundenDocFromTiles {
  documents: KundenDokument[];
  dokumentGroupNames: string[];
  tileTitle: string;
}

interface ReactNativeWebview {
  postMessage(message: string): void;
}

export function isReactNativeWebView(
  webview: unknown,
): webview is ReactNativeWebview {
  const webviewObject = Object(webview);
  return (
    webviewObject === webview &&
    'postMessage' in webviewObject &&
    typeof webviewObject.postMessage === 'function'
  );
}

function useDocuments(initialyShowDocs?: boolean, onlyUnreadDocs?: boolean) {
  const { welcomeStore } = useStore();
  const welcomeData = welcomeStore.getWelcomeData();

  const documentApi = useDocumentApi();

  const [selectedDocumentGroup, setSelectedDocumentGroup] = useState<
    string | null
  >(null);

  // group the documents by tile id
  const documentsGroupedByTileId = welcomeData?.tiles
    ?.map((tile: DocumentTile) =>
      tile.groupIds?.map((groupId: number) => {
        const groupedDocs = welcomeData?.documents?.find(
          documentGroup => documentGroup.gruppen_id === groupId,
        );

        return {
          documents: groupedDocs?.dokumente || [],
          dokumentGroupNames: groupedDocs?.name || '',
          tileTitle: tile.title || '',
        };
      }),
    )
    .flat();

  // group the documents by tile title and reduce the array of documents to one array
  const groupedDocuments = documentsGroupedByTileId?.reduce(
    (acc: any, documentGroup: any) => {
      if (!acc[documentGroup.tileTitle]) {
        acc[documentGroup.tileTitle] = [];
      }

      acc[documentGroup.tileTitle].push(documentGroup);
      return acc;
    },
    {},
  );

  // extract the documents from the grouped documents with an array of dokumentGroupNamess
  const documentsFromTiles = Object.keys(groupedDocuments || {}).map(
    (tileTitle: string): KundenDocFromTiles => {
      const flatDocuments = groupedDocuments[tileTitle]
        .map((documentGroup: any) => documentGroup.documents)
        .flat();

      const documentGroupNames = groupedDocuments[tileTitle].map(
        (documentGroup: any) => documentGroup.dokumentGroupNames,
      );

      return {
        documents: flatDocuments,
        dokumentGroupNames: documentGroupNames,
        tileTitle,
      };
    },
  );

  const getCurrentDocumentsByName = (
    data: KundenDocFromTiles[],
    DocumentGroupName: string | null,
  ) => {
    if (DocumentGroupName) {
      return data.find(
        documentGroup => documentGroup.tileTitle === DocumentGroupName,
      )?.documents;
    }
    return null;
  };

  // filter the documents by unread and remove the document group if there are no unread documents
  const unreadDocuments = documentsFromTiles
    ?.map((documentGroup: KundenDocFromTiles) => {
      const unreadDocs = documentGroup.documents.filter(
        (document: KundenDokument) => document.gelesen === false,
      );
      return {
        ...documentGroup,
        documents: unreadDocs,
      };
    })
    .filter(
      (documentGroup: KundenDocFromTiles) => documentGroup.documents.length > 0,
    );

  useEffect(() => {
    if (
      selectedDocumentGroup === null &&
      documentsFromTiles.length > 0 &&
      initialyShowDocs
    ) {
      const currentGroupe = onlyUnreadDocs
        ? unreadDocuments
        : documentsFromTiles;

      if (currentGroupe.length > 0) {
        setSelectedDocumentGroup(currentGroupe[0].tileTitle);
      }
    }
  }, [documentsFromTiles]);

  const selectedStuff = onlyUnreadDocs ? unreadDocuments : documentsFromTiles;

  const downloadDocument = async (kundendocument: KundenDokument) => {
    if (kundendocument && kundendocument.token) {
      if (
        'ReactNativeWebView' in window &&
        isReactNativeWebView(window.ReactNativeWebView)
      ) {
        // download in rn app
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            event: 'downloadKundenDokument',
            token: kundendocument.token,
          }),
        );
      } else {
        const res = await documentApi.downloadKundenDokument(
          kundendocument.token,
          {
            responseType: 'arraybuffer',
          },
        );
        const contentType =
          res.headers['content-type'] || 'application/octet-stream';
        const blob = new Blob([res.data], { type: contentType });

        const fileURL = window.URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = fileURL;
        tempLink.setAttribute('download', kundendocument.name || '');
        tempLink.click();
      }
      // mark document as read
      welcomeStore.setDocumentReadStatus(kundendocument.token);
    }
  };

  return {
    documentsFromTiles: selectedStuff,
    getCurrentDocumentsByName,
    selectedDocumentGroup,
    setSelectedDocumentGroup,
    downloadDocument,
  };
}

export default useDocuments;
